import { NumberFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SvbrgCompanyResult } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import {
  cellFactory,
  DataGridConfig,
  GridAlignmentType,
  gridColDefFactory,
  NULLISH_CELL_VALUE,
  NumberCell,
  UseDataGridConfig,
} from '~/components/DataGridCustom';

export const useSupplierStructuredSearchResultDataGrid: UseDataGridConfig<SvbrgCompanyResult> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.intake, {
    keyPrefix: 'SupplierStructuredSearchResultDataGrid.column',
  });

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });
    return {
      sortModel: [],
      columnVisibilityModel: {},
      columns: [
        createColumn({
          field: 'name',
          flex: 2,
          minWidth: 200,
          align: GridAlignmentType.LEFT,
          hideable: false,
          sortable: false,
        }),
        createColumn({
          field: 'legalForm',
          flex: 1,
          minWidth: 100,
          align: GridAlignmentType.LEFT,
          hideable: false,
          sortable: false,
          valueFormatter: (value) => value || NULLISH_CELL_VALUE,
        }),
        createColumn({
          field: 'status',
          flex: 1,
          minWidth: 80,
          align: GridAlignmentType.LEFT,
          hideable: false,
          sortable: false,
          valueFormatter: (value) => value || NULLISH_CELL_VALUE,
        }),
        createColumn({
          field: 'subject',
          flex: 1,
          minWidth: 100,
          align: GridAlignmentType.LEFT,
          hideable: false,
          sortable: false,
          valueFormatter: (value) => value || NULLISH_CELL_VALUE,
        }),
        createColumn({
          field: 'address',
          flex: 1,
          minWidth: 100,
          align: GridAlignmentType.LEFT,
          hideable: false,
          sortable: false,
          valueGetter: (_value, row) => row.companyAddress || NULLISH_CELL_VALUE,
        }),
        createColumn({
          field: 'directors',
          flex: 1,
          minWidth: 150,
          align: GridAlignmentType.LEFT,
          hideable: false,
          sortable: false,
          valueFormatter: (value: unknown) =>
            Array.isArray(value) && value.length > 0 ? value.join(', ') : NULLISH_CELL_VALUE,
        }),
        createColumn({
          field: 'northDataUrl',
          flex: 1,
          minWidth: 150,
          align: GridAlignmentType.LEFT,
          hideable: false,
          sortable: false,
          valueFormatter: (value) => value || NULLISH_CELL_VALUE,
        }),
        createColumn({
          field: 'similarityScore',
          flex: 1,
          minWidth: 100,
          align: GridAlignmentType.LEFT,
          hideable: false,
          sortable: false,
          renderCell: cellFactory(NumberCell, { preset: NumberFormatPreset.DECIMAL }),
        }),
      ],
    } as DataGridConfig<SvbrgCompanyResult>;
  }, [tColumn, t]);
};
